import {useState} from 'react';
import {StyleSheet} from 'react-native';
import {Modal, Portal} from 'react-native-paper';
import {useAlerts} from 'react-native-paper-alerts';
import {useMutation, useQueryClient} from 'react-query';
import {FirebaseError} from '@firebase/util';
import firestore from '@react-native-firebase/firestore';
import auth from '@react-native-firebase/auth';

import {useLayoutInfo} from '../../hooks/useLayoutInfo';
import {useAppSettings} from '../AppSettings';
import {
  DefaultFormikStoreValidator,
  FormikStoreForm,
  FormikStoreType,
  FormikStoreTypes,
} from '../FormikStore';
import {Person, PersonRoles} from '../../hooks/usePersonInfo';

export interface PersonEditorProps {
  dialogVisible: boolean;
  setDialogVisible: (visible: boolean) => void;
  person?: Person;
}

export function PersonEditor(props: PersonEditorProps): JSX.Element {
  const queryClient = useQueryClient();
  const Alert = useAlerts();
  const layoutInfo = useLayoutInfo();
  const appSettings = useAppSettings();

  const [validating, setValidating] = useState(false);

  const collectionName = 'people';
  const saveFirestoreObject = useMutation(
    async (firestoreObject: Person) => {
      // clean out properties that might be undefined
      if (firestoreObject.contactNote === undefined) {
        delete firestoreObject.contactNote;
      }
      if (props.person === undefined) {
        // emails always need to be lowercase everywhere
        firestoreObject.email = firestoreObject.email.toLowerCase();
        if (firestoreObject.emailSecondary !== undefined) {
          firestoreObject.emailSecondary =
            firestoreObject.emailSecondary.toLowerCase();
        }
        console.log('adding: ' + JSON.stringify(firestoreObject, null, 2));

        // create the auth user first, then drop the authUid in here
        let authUser;
        try {
          authUser = await auth().createUserWithEmailAndPassword(
            firestoreObject.email,
            'newpassword',
          );
        } catch (e) {
          const error = e as FirebaseError;
          console.log(
            `auth creation error: ${error.code} ${
              error.message
            } ${JSON.stringify(error)}`,
          );

          if (error.code === 'auth/email-already-in-use') {
            // If the email was already in use in auth, maybe the user was eager
            // and attempted to login / create an account before set up in firestore
            // we check that by verifying firestore has no records with that email
            const personSnapshot = await firestore()
              .collection('people')
              .where('email', '==', firestoreObject.email)
              .orderBy('firstName')
              .get();

            // If there is an auth record but no firestore record, it is safe to create
            // them here, then the next time they login, the login code in usePerson will
            // automatically bind them together by updating the authUid in firestore
            if (personSnapshot.size === 0) {
              console.log(
                'Auth entry exists, but no firestore entry. Creating person.',
              );
              firestoreObject.authUid =
                authUser?.user.uid ?? 'auth-user-creation-failed';
            } else {
              console.warn(
                'Auth entry exists, and firestore entry already exists. Not creating person.',
              );
              Alert.alert(
                appSettings.t('createAccountError'),
                appSettings.t(error.code ?? 'unknownError'),
                [{text: appSettings.t('OK')}],
              );
              throw e;
            }
          } else {
            Alert.alert(
              appSettings.t('createAccountError'),
              appSettings.t(error.code ?? 'unknownError'),
              [{text: appSettings.t('OK')}],
            );
            throw e;
          }
        }
        // console.log('got auth user: ' + JSON.stringify(authUser, null, 2));
        return (
          firestore()
            .collection(collectionName)
            // the types of .add and .set are incompatible, but we don't need the return value
            .add(firestoreObject) as Promise<unknown> as Promise<void>
        );
      }

      // console.log('saving: ' + JSON.stringify(firestoreObject, null, 2));
      return firestore()
        .collection(collectionName)
        .doc(props.person?.ref.id)
        .set(firestoreObject, {merge: true});
    },
    {
      onSettled: () => {
        // console.log('saving succeeded');
        return queryClient.invalidateQueries(collectionName);
      },
      onError: e => {
        Alert.alert('Error', 'There was an error while saving: ' + e, [
          {text: appSettings.t('OK')},
        ]);
      },
    },
  );

  const themedStyles = StyleSheet.create({
    formikStoreContent: {
      margin: 60,
      // flex: 1,
      borderColor: appSettings.currentTheme.colors.border,
      borderWidth: 2,
      backgroundColor: appSettings.currentTheme.colors.background,
      position: 'absolute',
      maxWidth: layoutInfo.screen.width - 120,
      maxHeight: layoutInfo.screen.height - 160,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  });

  let person: {[key: string]: FormikStoreType} = {
    // authUid: undefined, // FIXME we need to create an auth user during save!
    personId: Date.now(),
    firstName: undefined,
    lastName: undefined,
    email: undefined,
    roles: [],
  };

  if (props.person !== undefined) {
    person.authUid = props.person.authUid;
    person.personId = props.person.personId;
    if (props.person.firstName) {
      person.firstName = props.person.firstName;
    }
    if (props.person.lastName) {
      person.lastName = props.person.lastName;
    }
    if (props.person.email) {
      person.email = props.person.email.toLowerCase();
    }
    if (props.person.hubspotId) {
      person.hubspotId = props.person.hubspotId;
    }
    if (props.person.city) {
      person.city = props.person.city;
    }
    if (props.person.contactNote) {
      person.contactNote = props.person.contactNote;
    }
    if (props.person.country) {
      person.country = props.person.country;
    }
    if (props.person.emailSecondary) {
      person.emailSecondary = props.person.emailSecondary.toLowerCase();
    }
    if (props.person.phoneNumber1) {
      person.phoneNumber1 = props.person.phoneNumber1;
    }
    if (props.person.phoneNumber2) {
      person.phoneNumber2 = props.person.phoneNumber2;
    }
    if (props.person.state) {
      person.state = props.person.state;
    }
    if (props.person.streetAddress) {
      person.streetAddress = props.person.streetAddress;
    }
    if (props.person.zipCode) {
      person.zipCode = props.person.zipCode;
    }
    person.roles = props.person.roles ? [...props.person.roles] : [];
  }
  return (
    <Portal>
      <Modal
        contentContainerStyle={themedStyles.formikStoreContent}
        visible={props.dialogVisible}
        onDismiss={() => props.setDialogVisible(false)}>
        <FormikStoreForm
          objects={[
            {
              title: `${props.person ? 'Edit' : 'Create'} Person`,
              firestoreObject: person,
              fieldMetadata: [
                {
                  key: 'authUid',
                  name: 'Auth UID',
                  type: FormikStoreTypes.text,
                  required: false,
                  editable: false,
                  errorText: 'Auth UID is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'personId',
                  name: 'Person ID',
                  type: FormikStoreTypes.number,
                  required: true,
                  editable: false,
                  errorText: 'Person ID is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'firstName',
                  name: 'First Name',
                  type: FormikStoreTypes.text,
                  required: true,
                  errorText: 'First Name is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'lastName',
                  name: 'Last Name',
                  type: FormikStoreTypes.text,
                  required: true,
                  errorText: 'Last Name is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'email',
                  name: 'Email Address',
                  type: FormikStoreTypes.text, // FIXME need an email validator type
                  required: true,
                  errorText: 'Email Address is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'emailSecondary',
                  name: 'Secondary Email',
                  type: FormikStoreTypes.text,
                  required: false,
                  errorText: '',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'phoneNumber1',
                  name: 'Phone Number',
                  type: FormikStoreTypes.text,
                  required: false,
                  errorText: '',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'phoneNumber2',
                  name: 'Secondary Phone Number',
                  type: FormikStoreTypes.text,
                  required: false,
                  errorText: '',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'hubspotId',
                  name: 'Hubspot ID',
                  type: FormikStoreTypes.text,
                  required: false,
                  errorText: '',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'streetAddress',
                  name: 'Street Address',
                  type: FormikStoreTypes.text,
                  required: false,
                  errorText: '',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'city',
                  name: 'City',
                  type: FormikStoreTypes.text,
                  required: false,
                  errorText: '',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'state',
                  name: 'State',
                  type: FormikStoreTypes.text,
                  required: false,
                  errorText: '',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'zipCode',
                  name: 'Postal Code',
                  type: FormikStoreTypes.text,
                  required: false,
                  errorText: '',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'country',
                  name: 'Country',
                  type: FormikStoreTypes.text,
                  required: false,
                  errorText: '',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'roles',
                  name: 'App Roles',
                  type: FormikStoreTypes.arraySelect,
                  selectItems: {items: Object.values(PersonRoles)},
                  required: false,
                  errorText: '',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'contactNote',
                  name: 'Contact Notes',
                  type: FormikStoreTypes.textarea,
                  required: false,
                  errorText: '',
                  validator: DefaultFormikStoreValidator,
                },
              ],
            },
          ]}
          buttons={[
            {
              text: 'Cancel',
              validate: false,
              onPress: () => {
                props.setDialogVisible(false);
              },
            },
            {
              text: 'Save',
              validate: true,
              onPress: formObjects => {
                // console.log(
                //   'saving with ' +
                //     JSON.stringify(formObjects[0].firestoreObject, null, 2),
                // );
                setValidating(true);
                saveFirestoreObject.mutate(
                  formObjects[0].firestoreObject as unknown as Person,
                );
                props.setDialogVisible(false);
              },
            },
          ]}
          validateForm={validating}
        />
      </Modal>
    </Portal>
  );
}
